import {
  CancelledError,
  CrossOriginIsolationError,
  EngineError,
  ValidationError,
  WasmUnavailableError,
} from "../engine/types";

/**
 * Typed-error renderer. Routes by `err.code` (stable string) per
 * the spec's CLAUDE.md §5.3 convention; falls back to the class
 * name + message for unknown shapes.
 */
export function ErrorPanel({
  err,
  onDismiss,
}: {
  err: unknown;
  onDismiss?: () => void;
}) {
  const { title, body, kind } = describe(err);
  const tone =
    kind === "info"
      ? "border-paper-border bg-paper-panel text-paper-text dark:border-term-border dark:bg-term-panel dark:text-term-text"
      : kind === "warn"
        ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/40 dark:text-amber-200"
        : "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-700/50 dark:bg-rose-950/40 dark:text-rose-200";
  return (
    <div className={`border p-3 text-sm ${tone}`} role="alert">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{title}</p>
          <p className="mt-1 whitespace-pre-wrap">{body}</p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

function describe(err: unknown): {
  title: string;
  body: string;
  kind: "info" | "warn" | "error";
} {
  if (err instanceof CancelledError) {
    return {
      title: "Run cancelled",
      body: "The simulation was cancelled before it completed.",
      kind: "info",
    };
  }
  if (err instanceof CrossOriginIsolationError) {
    return {
      title: "Cross-origin isolation required",
      body: "The CPU-WASM backend requires the page to be cross-origin isolated (COOP/COEP). Re-run via `npm run dev` so Vite serves the required headers.",
      kind: "warn",
    };
  }
  if (err instanceof WasmUnavailableError) {
    return {
      title: "No engine backend available",
      body: `${err.message}\nTip: build the wasm bundle via \`./scripts/wasm-pack-build.sh release\` so /pkg/ is served by Vite.`,
      kind: "error",
    };
  }
  if (err instanceof ValidationError) {
    return {
      title: "Config rejected by validator",
      body: err.detail ?? err.message,
      kind: "warn",
    };
  }
  if (err instanceof EngineError) {
    return {
      title: err.code,
      body: err.detail ? `${err.message}\n${err.detail}` : err.message,
      kind: "error",
    };
  }
  if (err instanceof Error) {
    return { title: err.name || "Error", body: err.message, kind: "error" };
  }
  return { title: "Error", body: String(err), kind: "error" };
}
